import { axiosInstanceAdmin } from "api";
import { ContentState, EditorState, convertFromHTML, convertToRaw } from "draft-js";
import draftjsToHtml from "draftjs-to-html";
import { useLoginCheck } from "hooks/useLoginCheck";
import { useNavigatePage } from "hooks/useNavigatePage";
import { ChangeEvent, MouseEvent, useEffect, useRef, useState } from "react";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { useMutation, useQueryClient } from "react-query";

import PreviewNewsMobile from "../Preview/Mobile/PreviewNewsMobile";
import PreviewNewsPc from "../Preview/Pc/PreviewNewsPc";
import {
  BottomBtn,
  BottomWrapper,
  ContentWrapper,
  Modal,
  Name,
  Rectangle,
  SelectWrapper,
  TableWrapper,
  ThumbnailWrapper,
  Title,
  UploadImage,
  UploadImageLabel,
  Wrapper,
  SelectBoxWrapper,
} from "./style";
import { IMG_CDN_URL } from "constants/pathConstant";
import { dateFormat, formatDateForInput } from "components/dateFormat";
const UploadNews = ({ modifyData }) => {
  const loginCheck = useLoginCheck();
  const nav = useNavigatePage();
  const queryClient = useQueryClient();

  const imgRef = useRef<HTMLInputElement | null>(null);
  const imgRefKr = useRef<HTMLInputElement | null>(null);

  const [editorState, setEditorState] = useState<EditorState>(EditorState.createEmpty());
  const [editorStateKr, setEditorStateKr] = useState<EditorState>(EditorState.createEmpty());
  const [htmlString, setHtmlString] = useState<string>("");
  const [htmlStringKr, setHtmlStringKr] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [titleKr, setTitleKr] = useState<string>("");
  const [isActive, setIsActive] = useState<string>("true");
  const [imgSrcKr, setImgSrcKr] = useState<string>("");
  const [imgSrc, setImgSrc] = useState<string>("");
  const [region, setRegion] = useState<string>("Seoul");
  const [date, setDate] = useState<string>(dateFormat(new Date(), "-", 0));
  const [modal, setModal] = useState<boolean>(false);
  const [newsId, setNewsId] = useState<string>("");
  const [isImgEngChange, setIsImgEngChange] = useState<boolean>(false);
  const [isImgKrChange, setIsImgKrChange] = useState<boolean>(false);

  const [isUserInputRegion, setIsUserInputRegion] = useState<string>("Seoul");
  const [isUserInputDate, setIsUserInputDate] = useState<string>(new Date().toISOString());

  const thumbnailSrcKr = isImgKrChange ? imgSrcKr : `${IMG_CDN_URL}/${imgSrcKr}`;
  const thumbnailSrcEng = isImgEngChange ? imgSrc : `${IMG_CDN_URL}/${imgSrc}`;

  useEffect(() => {
    // 로그인 체크
    if (!loginCheck) return;
    loginCheck();
  }, []);

  useEffect(() => {
    if (!modifyData) return;
    setNewsId(modifyData.id);

    // 타이틀 초기값
    setTitleKr(modifyData.titleKr);
    setTitle(modifyData.titleEng);

    // 공개여부 초기값
    setIsActive(modifyData.isActive.toString());

    // 날짜 초기값
    setDate(dateFormat(modifyData.date));
    setIsUserInputDate("직접입력");

    // 지역 초기값
    setRegion(modifyData.region);
    setIsUserInputRegion("직접입력");

    // 에디터 초기값
    setHtmlString(modifyData.contentEng);
    setHtmlStringKr(modifyData.contentKr);
    const blocksFromHTMLEng = convertFromHTML(modifyData.contentEng);
    setEditorState(EditorState.createWithContent(ContentState.createFromBlockArray(blocksFromHTMLEng.contentBlocks)));
    const blocksFromHTMLKr = convertFromHTML(modifyData.contentKr);
    setEditorStateKr(EditorState.createWithContent(ContentState.createFromBlockArray(blocksFromHTMLKr.contentBlocks)));

    // 이미지 초기값
    setImgSrc(modifyData.imgPathEng);
    setImgSrcKr(modifyData.imgPathKr);
    setIsImgEngChange(false);
    setIsImgKrChange(false);
  }, [modifyData]);

  const updateTextDescription = async (state: EditorState, isKorean: boolean) => {
    // 에디터에 글쓰기 위한 state
    isKorean ? setEditorStateKr(state) : setEditorState(state);

    let html = draftjsToHtml(
      convertToRaw(isKorean ? editorStateKr.getCurrentContent() : editorState.getCurrentContent()),
    );

    // 불완전한 <p></p> 태그 수정
    html = html.replace(/<p><\/p>/g, "<br />");

    // html 기본 폰트사이즈 1rem, px을 rem으로 바꾸는 정규식
    let matchWord = html.match(/font-size: \d{1,2}px;/gi);
    if (matchWord) {
      matchWord = matchWord.map((font) => {
        const int = font.match(/\d{1,2}/g);
        return `font-size: ${int / 10}rem`;
      });

      html = html.replace(/font-size: \d{1,2}px;/gi, matchWord);
    }
    // ---------------------이슈 : &nbsp; 가 임의로 사라져버려서 띄어쓰기는 연속 2번이상 불가
    isKorean ? setHtmlStringKr(html) : setHtmlString(html);
  };

  // 이미지 업로드 핸들러
  const imgUploadHandler = (isKo: boolean, imgRef: any) => {
    const file = imgRef.current.files[0];
    // input에 안담기면 return
    if (!file) return;
    // 5MB보다 큰경우 return
    if (file.size / 1000 / 1000 > 5) {
      alert("파일 크기는 최대 5MB입니다.");
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        if (isKo) {
          setImgSrcKr(reader.result);
          setIsImgKrChange(true);
        } else {
          setImgSrc(reader.result);
          setIsImgEngChange(true);
        }
      }
    };
  };

  // region 선택 select handler
  const regionHandler = (e: ChangeEvent<HTMLInputElement>) => {
    if (isUserInputRegion === "직접입력") {
      setRegion(e.target.value);
    } else {
      setRegion(isUserInputRegion);
    }
  };

  // date 선택 select handler
  const dateHandler = (e: ChangeEvent<HTMLInputElement>) => {
    if (isUserInputDate === "직접입력") {
      setDate(dateFormat(e.target.value, "-", 0));
    } else {
      setDate(dateFormat(new Date(isUserInputDate), "-", 0));
    }
  };

  // 게시글 업로드 handler
  const config = {
    headers: { "Content-Type": "multipart/form-data" },
  };
  const { mutate } = useMutation((data: any) => axiosInstanceAdmin.post("/news", data, config), {
    onSuccess: () => queryClient.invalidateQueries(["manage_news0"]),
  });

  const { mutate: updateMutate } = useMutation((data: any) => axiosInstanceAdmin.put("/news", data, config), {
    onSuccess: () => queryClient.invalidateQueries(["manage_news0"]),
  });

  const upLoadHandler = () => {
    if (!title || !htmlString || !region || !titleKr || !htmlStringKr) {
      alert("빠진 내용 없이 다 채워주세요");
      return;
    }

    const formData = new FormData();
    const content = htmlString;
    const contentKr = htmlStringKr;
    const data = {
      id: modifyData && newsId,
      titleEng: title,
      contentEng: content,
      region,
      isActive: isActive === "true",
      titleKr,
      contentKr,
      isImgEngChange,
      isImgKrChange,
      imgSrc,
      imgSrcKr,
    };

    const fixHourDate = dateFormat(date, "-", -9);

    const fileEng = imgRef.current?.files?.[0] || null;
    const fileKr = imgRefKr.current?.files?.[0] || null;

    if (fileEng) {
      formData.append("imgEng", fileEng);
    }

    if (fileKr) {
      formData.append("imgKr", fileKr);
    }

    formData.append("data", JSON.stringify(data));
    formData.append("date", fixHourDate);

    if (modifyData) {
      // 수정할 경우
      if (window.confirm("수정하시겠습니까?")) {
        updateMutate(formData);
        nav("/admin-sketch/news/manage/0");
      }
    } else {
      // 새 글을 작성하는 경우
      if (window.confirm("등록하시겠습니까?")) {
        mutate(formData);
        nav("/admin-sketch/news/manage/0");
      }
    }
  };

  const previewHandler = () => {
    setModal(true);
  };

  const modalCloseHandler = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      setModal(false);
    }
  };

  return (
    <Wrapper>
      <Name>News & Event</Name>
      <TableWrapper>
        <span>
          <Rectangle />
          작성하기
        </span>
        <div>
          <SelectBoxWrapper>
            <div>작성일</div>
            <SelectWrapper>
              <select
                onChange={(e) => {
                  setIsUserInputDate(e.target.value);
                }}
                value={isUserInputDate}
              >
                <option value={new Date().toISOString()}>{dateFormat(new Date(), "-", 0).substring(0, 16)}</option>
                <option value={"직접입력"}>직접입력</option>
              </select>
              {isUserInputDate === "직접입력" && (
                <input type="datetime-local" value={formatDateForInput(date)} onChange={(e) => dateHandler(e)} />
              )}
            </SelectWrapper>
          </SelectBoxWrapper>
          <div>
            <SelectBoxWrapper>
              <div>위치</div>
              <SelectWrapper>
                <select onChange={(e) => setIsUserInputRegion(e.target.value)} value={isUserInputRegion}>
                  <option value="Seoul">Seoul</option>
                  <option value={"직접입력"}>직접입력</option>
                </select>
                {isUserInputRegion === "직접입력" && (
                  <input type="text" onChange={(e) => regionHandler(e)} value={region} />
                )}
              </SelectWrapper>
            </SelectBoxWrapper>
            <SelectBoxWrapper>
              <div>공개 여부</div>
              <SelectWrapper>
                <select onChange={(e: ChangeEvent<HTMLSelectElement>) => setIsActive(e.target.value)} value={isActive}>
                  <option value={"true"}>공개</option>
                  <option value={"false"}>비공개</option>
                </select>
              </SelectWrapper>
            </SelectBoxWrapper>
          </div>
        </div>
      </TableWrapper>
      <div>
        <ContentWrapper>
          <span>
            <Rectangle />
            내용(한국어)
          </span>
          <div>
            <Title
              onChange={(e: ChangeEvent<HTMLInputElement>) => setTitleKr(e.target.value)}
              defaultValue={modifyData?.titleKr || ""}
              placeholder="제목을 입력해주세요"
            />
          </div>
          <Editor
            placeholder="내용을 작성해주세요(이모지 첨부시 업로드 불가)"
            editorState={editorStateKr}
            onEditorStateChange={(state: EditorState) => updateTextDescription(state, true)}
            localization={{ locale: "ko" }}
            editorStyle={{
              height: "64rem",
              width: "80rem",
              border: "3px solid lightgray",
              padding: "20px",
              fontSize: "1.6rem",
            }}
            toolbar={{
              // inDropdown: 해당 항목과 관련된 항목을 드롭다운으로 나타낼것인지
              list: { inDropdown: true },
              textAlign: { inDropdown: true },
              link: { inDropdown: true },
            }}
          />
        </ContentWrapper>
        <ContentWrapper>
          <span>
            <Rectangle />
            내용(영어)
          </span>
          <div>
            <Title
              onChange={(e: ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
              defaultValue={modifyData?.titleEng || ""}
              placeholder="제목을 입력해주세요"
            />
          </div>
          <Editor
            placeholder="내용을 작성해주세요(이모지 첨부시 업로드 불가)"
            editorState={editorState}
            onEditorStateChange={(state) => updateTextDescription(state, false)}
            localization={{ locale: "ko" }}
            editorStyle={{
              height: "64rem",
              width: "80rem",
              border: "3px solid lightgray",
              padding: "20px",
              fontSize: "1.6rem",
            }}
            toolbar={{
              // inDropdown: 해당 항목과 관련된 항목을 드롭다운으로 나타낼것인지
              list: { inDropdown: true },
              textAlign: { inDropdown: true },
              link: { inDropdown: true },
            }}
          />
        </ContentWrapper>
      </div>

      <div>
        <ThumbnailWrapper>
          <div>한국어 썸네일</div>
          <div>
            <img src={thumbnailSrcKr} alt="thumbnail" />
            <UploadImage
              type="file"
              id="imgKr"
              onChange={() => imgUploadHandler(true, imgRefKr)}
              accept="image/*"
              ref={imgRefKr}
            />
            <UploadImageLabel htmlFor="imgKr">파일 선택</UploadImageLabel>
          </div>
        </ThumbnailWrapper>
        <ThumbnailWrapper>
          <div>영어 썸네일</div>
          <div>
            <img src={thumbnailSrcEng} alt="thumbnail" />
            <UploadImage
              type="file"
              id="img"
              onChange={() => imgUploadHandler(false, imgRef)}
              accept="image/*"
              ref={imgRef}
            />
            <UploadImageLabel htmlFor="img">파일 선택</UploadImageLabel>
          </div>
        </ThumbnailWrapper>
      </div>

      <BottomWrapper>
        <div>
          <BottomBtn onClick={() => nav(-1)}>이전</BottomBtn>
          <BottomBtn onClick={() => nav("/admin-sketch/news/manage/0")}>목록</BottomBtn>
        </div>
        <div>
          <BottomBtn onClick={previewHandler} bgColor={"#727582"} fontColor={"white"}>
            미리보기
          </BottomBtn>
          <BottomBtn onClick={upLoadHandler} bgColor={"#3D4359"} fontColor={"white"}>
            저장
          </BottomBtn>
        </div>
      </BottomWrapper>

      {/* 미리보기 모달 */}
      {modal && (
        <Modal onClick={modalCloseHandler}>
          <PreviewNewsPc
            title={title}
            date={date}
            region={region}
            imgSrc={thumbnailSrcEng}
            htmlString={htmlString}
            imgSrcKr={thumbnailSrcKr}
            htmlStringKr={htmlStringKr}
            titleKr={titleKr}
          />
          <PreviewNewsMobile
            title={title}
            date={date}
            region={region}
            imgSrc={thumbnailSrcEng}
            htmlString={htmlString}
            imgSrcKr={thumbnailSrcKr}
            htmlStringKr={htmlStringKr}
            titleKr={titleKr}
          />
        </Modal>
      )}
    </Wrapper>
  );
};

export default UploadNews;
